import type { ErrorEvent, EventHint } from '@sentry/react';

const HTTP_ERROR_FINGERPRINT_PREFIX = 'http-error';
const UUID_PATH_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NUMERIC_PATH_SEGMENT = /^\d+$/;

export type HttpErrorLike = Error & {
  status: number;
  statusText?: string;
  data?: unknown;
  response: {
    url: string;
  };
  cause: {
    code: string;
    url: string;
    status: number;
    message: string;
    method?: string;
  };
};

type HttpErrorEventOptions = {
  team?: string;
  topic?: string;
};

export const HTTP_ERROR_PREPROCESS_INTEGRATION_NAME = 'HttpErrorPreprocess';

type HttpErrorMetadata = {
  status: number;
  method: string;
  url: string;
  route: string;
  code: string;
  backendMessage: string;
  title: string;
  classification: 'client' | 'server';
};

const getRouteFromUrl = (url: string) => {
  if (!url) return 'unknown-route';

  try {
    const parsed = new URL(url, globalThis.location?.origin);
    return normalizePathname(parsed.pathname || '/');
  } catch {
    return normalizePathname(url.split('?')[0] || 'unknown-route');
  }
};

const normalizePathSegment = (segment: string) => {
  if (isIdLikePathSegment(segment)) {
    return ':id';
  }

  return segment;
};

const isIdLikePathSegment = (segment: string) => {
  if (!segment) {
    return false;
  }

  return UUID_PATH_SEGMENT.test(segment) || NUMERIC_PATH_SEGMENT.test(segment);
};

const normalizePathname = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return pathname || '/';
  }

  const normalizedSegments = pathname.split('/').map(normalizePathSegment);
  return normalizedSegments.join('/') || '/';
};

const getBackendMessage = (error: HttpErrorLike) => {
  if (typeof error.data === 'string' && error.data.trim()) {
    return error.data;
  }

  if (
    error.data &&
    typeof error.data === 'object' &&
    'message' in error.data &&
    typeof error.data.message === 'string'
  ) {
    return error.data.message;
  }

  return error.cause.message || error.statusText || 'Unknown HTTP error';
};

export const isHttpError = (error: unknown): error is HttpErrorLike => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as Partial<HttpErrorLike> & { name?: string };

  return (
    candidate.name === 'HttpError' &&
    typeof candidate.status === 'number' &&
    !!candidate.response &&
    typeof candidate.response.url === 'string' &&
    !!candidate.cause &&
    typeof candidate.cause.code === 'string'
  );
};

export const getHttpErrorMetadata = (error: HttpErrorLike): HttpErrorMetadata => {
  const method = error.cause.method || 'UNKNOWN';
  const route = getRouteFromUrl(error.cause.url || error.response.url);
  const code = error.cause.code || 'HttpError';
  const backendMessage = getBackendMessage(error);
  const classification = error.status >= 500 ? 'server' : 'client';
  const title = `HTTP ${error.status} ${method} ${route}`;

  return {
    status: error.status,
    method,
    url: error.cause.url || error.response.url,
    route,
    code,
    backendMessage,
    title,
    classification,
  };
};

export const getHttpErrorFingerprint = (error: HttpErrorLike) => {
  const metadata = getHttpErrorMetadata(error);
  const fingerprint = [
    HTTP_ERROR_FINGERPRINT_PREFIX,
    metadata.classification,
    String(metadata.status),
    metadata.method,
    metadata.route,
    metadata.code,
  ];

  return fingerprint;
};

export const applyHttpErrorToEvent = (
  event: ErrorEvent,
  error: HttpErrorLike,
  options: HttpErrorEventOptions = {}
): ErrorEvent => {
  const metadata = getHttpErrorMetadata(error);
  const values = [...(event.exception?.values ?? [])];

  if (values.length > 0) {
    const lastIndex = values.length - 1;
    values[lastIndex] = {
      ...values[lastIndex],
      type: 'HttpError',
      value: metadata.title,
    };
  }

  return {
    ...event,
    message: event.message || metadata.title,
    exception: values.length > 0 ? { ...event.exception, values } : event.exception,
    fingerprint: event.fingerprint?.length ? event.fingerprint : getHttpErrorFingerprint(error),
    tags: {
      ...event.tags,
      topic: options.topic || event.tags?.topic || 'http',
      'error.kind': 'http',
      'http.status': String(metadata.status),
      'http.method': metadata.method,
      'http.route': metadata.route,
      'http.class': metadata.classification,
      'http.code': metadata.code,
    },
    contexts: {
      ...event.contexts,
      httpError: {
        status: metadata.status,
        method: metadata.method,
        route: metadata.route,
        url: metadata.url,
        code: metadata.code,
        backendMessage: metadata.backendMessage,
      },
    },
  };
};

export const createHttpErrorPreprocessIntegration = () => ({
  name: HTTP_ERROR_PREPROCESS_INTEGRATION_NAME,
  preprocessEvent(event: ErrorEvent, hint: EventHint) {
    if (!isHttpError(hint.originalException)) {
      return;
    }

    const normalizedEvent = applyHttpErrorToEvent(event, hint.originalException, {
      team: typeof event.tags?.team === 'string' ? event.tags.team : undefined,
      topic: typeof event.tags?.topic === 'string' ? event.tags.topic : undefined,
    });

    Object.assign(event, normalizedEvent);
  },
});
